import { getInterpretation } from '@/utils/diagnosticInterpretations';
import { RefreshCw } from 'lucide-react';

/**
 * Dynamic one-sentence "energy state" labels by result type.
 * Rendered as a highlighted badge ABOVE the description paragraphs.
 * Because the badge is now separate, paragraphs[0] (which is stored lowercase)
 * is capitalised when rendered as a standalone paragraph.
 */
const RESULT_PREFIXES = {
  a: 'Tavo pasirinkimuose vyrauja aktyvi, pozityvi šio skaičiaus energija',
  b: 'Tavo pasirinkimuose dominuoja pasyvesnė, labiau atsiribojusi būsena',
  c: 'Tavo pasirinkimai rodo lankstų balansą ir laviravimą per vidurį',
  tie: 'Tavo atsakymai pasiskirstė visiškai po lygiai – tai potencialo kryžkelė',
};

const COLOR_MAP = {
  emerald: { border: 'border-emerald-500/40', title: 'text-emerald-400', chipBorder: 'border-emerald-500/40', chipBg: 'bg-emerald-500/10', chipText: 'text-emerald-200', dot: 'bg-emerald-400' },
  amber: { border: 'border-amber-500/40', title: 'text-amber-400', chipBorder: 'border-amber-500/40', chipBg: 'bg-amber-500/10', chipText: 'text-amber-200', dot: 'bg-amber-400' },
  purple: { border: 'border-purple-500/40', title: 'text-purple-400', chipBorder: 'border-purple-500/40', chipBg: 'bg-purple-500/10', chipText: 'text-purple-200', dot: 'bg-purple-400' },
  blue: { border: 'border-blue-500/40', title: 'text-blue-400', chipBorder: 'border-blue-500/40', chipBg: 'bg-blue-500/10', chipText: 'text-blue-200', dot: 'bg-blue-400' },
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export const ResultInterpretation = ({ baseNumber, resultType, onTryAnotherDate }) => {
  const data = getInterpretation(baseNumber, resultType);
  if (!data) return null;

  const c = COLOR_MAP[data.color] || COLOR_MAP.blue;
  const badgeLabel = RESULT_PREFIXES[resultType] || '';

  return (
    <div
      className={`rounded-lg border-2 ${c.border} bg-[#0f0f23]/50 p-5`}
      data-testid="result-interpretation"
    >
      <h3 className={`font-display text-base sm:text-lg ${c.title} mb-4 text-center`}>
        {data.title}
      </h3>

      {badgeLabel && (
        <div
          className={`flex items-start gap-2.5 rounded-lg border ${c.chipBorder} ${c.chipBg} px-3.5 py-2.5 mb-5`}
          data-testid="result-energy-badge"
        >
          <span className={`w-2 h-2 mt-1.5 rounded-full ${c.dot} flex-shrink-0 animate-pulse`} />
          <span className={`text-xs sm:text-sm font-semibold leading-snug ${c.chipText}`}>
            {badgeLabel}
          </span>
        </div>
      )}

      <div className="space-y-4">
        {data.paragraphs.map((p, i) => (
          <p key={i} className="text-foreground/85 text-sm leading-relaxed whitespace-pre-line">
            {i === 0 ? capitalize(p) : p}
          </p>
        ))}
      </div>

      <button
        type="button"
        onClick={onTryAnotherDate}
        className="group mt-6 w-full flex items-center justify-center gap-2 rounded-lg border border-[#d4af37]/40 bg-[#d4af37]/5 hover:bg-[#d4af37]/15 text-[#d4af37] font-medium py-3 transition-colors duration-200"
        data-testid="try-another-date-btn"
      >
        <RefreshCw className="w-4 h-4 transition-transform duration-500 ease-in-out group-hover:-rotate-180" />
        Bandyti kitą datą
      </button>
    </div>
  );
};
